import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, API } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { AppHeader } from "@/components/AppHeader";
import { PdfViewer } from "@/components/PdfViewer";
import { PythonQuickRef } from "@/components/PythonQuickRef";
import { toast } from "sonner";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Download,
  Upload,
  Sparkles,
  RotateCcw,
  Loader2,
  FileText,
  Presentation,
  Trash2,
  MessageCircle,
  ClipboardList,
  Volume2,
  VolumeX,
  Play,
  Pause,
} from "lucide-react";

const SUBJECT_COLOR = {
  Computer: "hsl(200 90% 50%)",
  English: "hsl(10 85% 62%)",
};

function SlideRenderer({ slide, accent }) {
  if (!slide) return null;
  const t = slide.type;
  if (t === "title") {
    return (
      <div className="h-full flex flex-col justify-center relative overflow-hidden bg-[#0F1F35] text-white p-10 sm:p-16 rounded-3xl">
        <div
          className="absolute -right-20 -top-20 w-72 h-72 rounded-full blur-2xl opacity-60"
          style={{ background: accent }}
        />
        <div className="absolute -left-16 -bottom-20 w-64 h-64 rounded-full bg-[hsl(var(--brimi-coral))] blur-2xl opacity-40" />
        <div className="relative">
          {slide.eyebrow && (
            <div className="text-xs font-black uppercase tracking-[0.25em] mb-4" style={{ color: accent }}>
              {slide.eyebrow}
            </div>
          )}
          <h1 className="text-5xl sm:text-6xl font-black leading-[1.05] mb-4">
            {slide.title}
          </h1>
          {slide.subtitle && (
            <p className="text-xl sm:text-2xl text-white/75 max-w-2xl">
              {slide.subtitle}
            </p>
          )}
        </div>
      </div>
    );
  }
  if (t === "code") {
    return (
      <div className="h-full bg-card border border-border rounded-3xl p-8 sm:p-10 relative overflow-y-auto">
        <div className="absolute top-0 bottom-0 left-0 w-2 rounded-l-3xl" style={{ background: accent }} />
        <h2 className="text-3xl font-black mb-5 pl-2">{slide.title}</h2>
        <pre className="bg-[#0F1F35] text-white text-sm sm:text-base rounded-2xl p-5 overflow-x-auto font-mono whitespace-pre">
          {slide.code}
        </pre>
        {slide.bullets?.length > 0 && (
          <ul className="mt-5 space-y-2 pl-2">
            {slide.bullets.map((b, i) => (
              <li key={i} className="flex gap-3 text-base text-muted-foreground">
                <span style={{ color: accent }}>▸</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }
  if (t === "quote") {
    return (
      <div className="h-full bg-card border border-border rounded-3xl p-10 sm:p-14 relative">
        <div className="absolute top-0 bottom-0 left-0 w-2 rounded-l-3xl" style={{ background: accent }} />
        <h2 className="text-2xl font-bold mb-6 pl-2">{slide.title}</h2>
        <blockquote className="text-2xl sm:text-4xl font-bold italic leading-snug pl-2" style={{ color: accent }}>
          “{slide.quote}”
        </blockquote>
        {slide.author && (
          <div className="mt-4 text-base text-muted-foreground font-semibold pl-2">— {slide.author}</div>
        )}
        {slide.explanation && (
          <p className="mt-6 text-base sm:text-lg text-foreground/85 leading-relaxed pl-2">
            {slide.explanation}
          </p>
        )}
      </div>
    );
  }
  if (t === "summary") {
    return (
      <div className="h-full bg-[#0F1F35] text-white rounded-3xl p-10 sm:p-14 relative overflow-hidden">
        <div className="absolute -right-10 -bottom-10 w-80 h-80 rounded-full bg-[hsl(var(--brimi-coral))]/30 blur-3xl" />
        <h2 className="text-4xl font-black mb-6 relative">{slide.title || "Key Takeaways"}</h2>
        <ul className="space-y-3 relative">
          {(slide.bullets || []).map((b, i) => (
            <li key={i} className="flex gap-3 text-lg sm:text-xl">
              <span className="text-[hsl(var(--brimi-yellow))]">✓</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  // content (default)
  return (
    <div className="h-full bg-card border border-border rounded-3xl p-8 sm:p-12 relative overflow-y-auto">
      <div className="absolute top-0 bottom-0 left-0 w-2 rounded-l-3xl" style={{ background: accent }} />
      <h2 className="text-3xl sm:text-4xl font-black mb-6 pl-2">{slide.title}</h2>
      <ul className="space-y-3 pl-2">
        {(slide.bullets || []).map((b, i) => (
          <li key={i} className="flex gap-3 text-base sm:text-lg leading-relaxed">
            <span className="font-black mt-0.5" style={{ color: accent }}>•</span>
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function MaterialsPage() {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOwner = !!user?.is_owner;
  const [material, setMaterial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [current, setCurrent] = useState(0);
  const [regenerating, setRegenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // ---- Narration (TTS) ----
  const audioRef = useRef(null);
  const [autoNarrate, setAutoNarrate] = useState(() => {
    return localStorage.getItem("brimi_auto_narrate") === "true";
  });
  const [playing, setPlaying] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [slowGen, setSlowGen] = useState(false);  // True if loading > 2s (cold cache)

  const toggleAutoNarrate = useCallback(() => {
    setAutoNarrate((prev) => {
      const next = !prev;
      localStorage.setItem("brimi_auto_narrate", String(next));
      return next;
    });
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/materials/${topicId}`);
      setMaterial(data);
      setCurrent(0);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load material");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get(`/materials/${topicId}`)
      .then(({ data }) => {
        if (cancelled) return;
        setMaterial(data);
        setCurrent(0);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e.isSubscriptionError) {
          setError("Your free trial has ended. Subscribe to view course material.");
        } else {
          setError(e?.response?.data?.detail || "Failed to load material");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [topicId]);

  // ---- Audio: build src for current slide and manage playback ----
  const audioSrc = (() => {
    if (!material || material.slides?.length === 0) return null;
    const token = localStorage.getItem("brimi_token");
    if (!token) return null;
    return `${API}/materials/${topicId}/slides/${current}/audio?auth=${encodeURIComponent(
      token,
    )}`;
  })();

  // Pause + reset audio whenever slide changes; auto-play if toggle on
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !audioSrc) return;
    el.pause();
    el.currentTime = 0;
    setPlaying(false);
    setSlowGen(false);
    if (autoNarrate) {
      setAudioLoading(true);
      const p = el.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          // Autoplay blocked by browser; user can click Play manually
          setAudioLoading(false);
        });
      }
    }
  }, [current, audioSrc, autoNarrate]);

  // Show 'Generating narration…' hint after 2s of loading (cold cache)
  useEffect(() => {
    if (!audioLoading) {
      setSlowGen(false);
      return;
    }
    const t = setTimeout(() => setSlowGen(true), 2000);
    return () => clearTimeout(t);
  }, [audioLoading]);

  const togglePlay = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      setAudioLoading(true);
      const p = el.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          toast.error("Couldn't play audio. Try clicking Play again.");
          setAudioLoading(false);
        });
      }
    } else {
      el.pause();
    }
  }, []);

  const accent = SUBJECT_COLOR[material?.subject] || "hsl(200 90% 50%)";

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const { data } = await api.post(`/materials/${topicId}/regenerate`);
      setMaterial(data);
      setCurrent(0);
      toast.success("Fresh slides generated! 🎉");
    } catch (e) {
      if (!e.isSubscriptionError)
        toast.error(e?.response?.data?.detail || "Failed to regenerate");
    } finally {
      setRegenerating(false);
    }
  };

  const downloadAIPPTX = () => {
    const token = localStorage.getItem("brimi_token");
    const url = `${API}/materials/${topicId}/download.pptx?auth=${encodeURIComponent(token)}`;
    window.location.href = url;
  };

  const downloadUploaded = () => {
    const token = localStorage.getItem("brimi_token");
    const url = `${API}/materials/${topicId}/file?auth=${encodeURIComponent(token)}`;
    // Use an anchor with `download` so PDFs (served inline) still save instead
    // of replacing the current page.
    const a = document.createElement("a");
    a.href = url;
    a.download = material?.uploaded_file?.original_filename || "material";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["pptx", "pdf"].includes(ext)) {
      toast.error("Only .pptx or .pdf files are allowed");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File too large (max 25 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/materials/${topicId}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMaterial(data);
      toast.success("Uploaded! Students will see your file by default. ✨");
    } catch (err) {
      if (!err.isSubscriptionError)
        toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeUpload = async () => {
    if (!window.confirm("Remove your uploaded file and use AI slides instead?")) return;
    try {
      const { data } = await api.delete(`/materials/${topicId}/upload`);
      setMaterial(data);
      toast.success("Reverted to AI-generated slides.");
    } catch (e) {
      toast.error("Could not remove upload");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <AppHeader />
        <main className="max-w-3xl mx-auto px-6 py-16 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-[hsl(var(--brimi-blue))]/15 flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-[hsl(var(--brimi-blue))] animate-spin" />
          </div>
          <h2 className="text-2xl font-black mb-2">Generating your study notes…</h2>
          <p className="text-muted-foreground">
            Brimi is crafting slides tailored to this chapter (first time only).
          </p>
        </main>
      </div>
    );
  }

  if (error || !material) {
    return (
      <div className="min-h-screen">
        <AppHeader />
        <main className="max-w-2xl mx-auto px-6 py-16 text-center">
          <h2 className="text-2xl font-black mb-3">Couldn&apos;t load slides</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <button
            data-testid="materials-retry-btn"
            onClick={load}
            className="pill-btn bg-[hsl(var(--brimi-blue))] text-white px-6 py-3"
          >
            <RotateCcw className="w-4 h-4" /> Try again
          </button>
        </main>
      </div>
    );
  }

  const slides = material.slides || [];
  const slide = slides[current];
  const hasUpload = !!material.uploaded_file;
  const isPdf = material.source === "pdf" && hasUpload;
  const pdfUrl = isPdf
    ? `${API}/materials/${topicId}/file?auth=${encodeURIComponent(
        localStorage.getItem("brimi_token") || ""
      )}`
    : null;

  return (
    <div className="min-h-screen">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <button
          onClick={() => navigate(-1)}
          data-testid="materials-back-btn"
          className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div
              className="text-xs uppercase tracking-widest font-bold mb-2"
              style={{ color: accent }}
            >
              {material.subject} · Class {material.class_id} · Course material
            </div>
            <h1 className="text-3xl sm:text-4xl font-black">
              {material.topic_title}
            </h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="materials-chat-link"
              onClick={async () => {
                try {
                  const { data } = await api.post("/chat/start", { topic_id: topicId });
                  navigate(`/tutor/${data.session_id}`);
                } catch {
                  toast.error("Couldn't open chat");
                }
              }}
              className="pill-btn bg-foreground/5 hover:bg-foreground/10 text-sm px-4 py-2"
            >
              <MessageCircle className="w-4 h-4" /> Chat
            </button>
            <Link
              data-testid="materials-quiz-link"
              to={`/quiz/${topicId}`}
              className="pill-btn bg-foreground/5 hover:bg-foreground/10 text-sm px-4 py-2"
            >
              <ClipboardList className="w-4 h-4" /> Quiz
            </Link>
          </div>
        </div>

        {/* Uploaded banner */}
        {hasUpload && (
          <div
            data-testid="uploaded-banner"
            className="mb-5 tactile-card p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3"
          >
            <div className="w-10 h-10 rounded-xl bg-[hsl(var(--brimi-mint))]/20 text-[hsl(var(--brimi-mint))] flex items-center justify-center shrink-0">
              <FileText className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">
                Custom upload
              </div>
              <div className="font-semibold truncate">
                {material.uploaded_file.original_filename}
              </div>
              <div className="text-xs text-muted-foreground">
                Uploaded by {material.uploaded_file.uploaded_by?.name} •{" "}
                {(material.uploaded_file.size / 1024).toFixed(0)} KB
              </div>
            </div>
            <div className="flex gap-2">
              <button
                data-testid="download-uploaded-btn"
                onClick={downloadUploaded}
                className="pill-btn bg-[hsl(var(--brimi-mint))] hover:bg-[hsl(var(--brimi-mint))]/90 text-white text-sm px-4 py-2"
              >
                <Download className="w-4 h-4" /> Download
              </button>
              {isOwner && (
                <button
                  data-testid="remove-uploaded-btn"
                  onClick={removeUpload}
                  className="pill-btn bg-destructive/10 hover:bg-destructive/20 text-destructive text-sm px-3 py-2"
                  title="Remove upload"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Slide stage OR PDF viewer */}
        {isPdf ? (
          <PdfViewer
            topicId={topicId}
            pdfUrl={pdfUrl}
            fileName={material.uploaded_file.original_filename}
            sizeKb={Math.round((material.uploaded_file.size || 0) / 1024)}
            pageCount={material.uploaded_file.page_count}
            youtubeLinks={material.uploaded_file.youtube_links}
            accent={accent}
          />
        ) : slides.length > 0 ? (
          <div className="tactile-card p-3 sm:p-5">
            <div className="aspect-[16/9] w-full" data-testid="slide-stage">
              <SlideRenderer slide={slide} accent={accent} />
            </div>

            {/* Narration controls */}
            <div
              data-testid="narration-bar"
              className="mt-4 flex items-center justify-between gap-3 px-4 py-3 rounded-2xl bg-muted/50 border border-border"
            >
              <div className="flex items-center gap-2 min-w-0">
                <button
                  data-testid="play-audio-btn"
                  onClick={togglePlay}
                  disabled={!audioSrc}
                  className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-[hsl(var(--brimi-blue))] hover:bg-[hsl(var(--brimi-blue))]/90 text-white disabled:opacity-40"
                  title={playing ? "Pause narration" : "Play narration"}
                >
                  {audioLoading && !playing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : playing ? (
                    <Pause className="w-4 h-4" />
                  ) : (
                    <Play className="w-4 h-4 ml-0.5" />
                  )}
                </button>
                <div className="min-w-0">
                  <div className="text-xs font-black uppercase tracking-widest text-muted-foreground">
                    Narration
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {audioLoading
                      ? slowGen
                        ? "Generating narration… (cached after this)"
                        : "Loading…"
                      : playing
                        ? "Speaking…"
                        : "Click play to hear this slide"}
                  </div>
                </div>
              </div>

              <button
                data-testid="auto-narrate-toggle"
                onClick={toggleAutoNarrate}
                className={`pill-btn shrink-0 text-xs px-3 py-2 ${
                  autoNarrate
                    ? "bg-[hsl(var(--brimi-mint))] text-white hover:bg-[hsl(var(--brimi-mint))]/90"
                    : "bg-foreground/5 hover:bg-foreground/10 text-foreground"
                }`}
                title="Auto-play narration when slide changes"
              >
                {autoNarrate ? (
                  <Volume2 className="w-3.5 h-3.5" />
                ) : (
                  <VolumeX className="w-3.5 h-3.5" />
                )}
                <span className="hidden sm:inline">
                  Auto-narrate {autoNarrate ? "ON" : "OFF"}
                </span>
                <span className="sm:hidden">{autoNarrate ? "ON" : "OFF"}</span>
              </button>

              <audio
                ref={audioRef}
                src={audioSrc || undefined}
                preload="none"
                onPlay={() => {
                  setPlaying(true);
                  setAudioLoading(false);
                }}
                onPause={() => setPlaying(false)}
                onEnded={() => {
                  setPlaying(false);
                  setAudioLoading(false);
                }}
                onError={() => {
                  setPlaying(false);
                  setAudioLoading(false);
                }}
                onWaiting={() => setAudioLoading(true)}
                onCanPlay={() => setAudioLoading(false)}
                className="hidden"
                data-testid="narration-audio"
              />
            </div>

            {/* Controls */}
            <div className="mt-4 flex items-center justify-between gap-3">
              <button
                data-testid="slide-prev"
                onClick={() => setCurrent((c) => Math.max(0, c - 1))}
                disabled={current === 0}
                className="pill-btn bg-foreground/5 hover:bg-foreground/10 px-4 py-2 text-sm disabled:opacity-40"
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </button>

              <div className="flex items-center gap-2">
                <div className="text-sm font-bold">
                  <span style={{ color: accent }}>{current + 1}</span>
                  <span className="text-muted-foreground"> / {slides.length}</span>
                </div>
              </div>

              <button
                data-testid="slide-next"
                onClick={() => setCurrent((c) => Math.min(slides.length - 1, c + 1))}
                disabled={current >= slides.length - 1}
                className="pill-btn bg-[hsl(var(--brimi-blue))] hover:bg-[hsl(var(--brimi-blue))]/90 text-white px-4 py-2 text-sm disabled:opacity-40"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Thumbnails */}
            <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
              {slides.map((s, i) => (
                <button
                  key={i}
                  data-testid={`slide-thumb-${i}`}
                  onClick={() => setCurrent(i)}
                  className={`shrink-0 w-20 h-14 rounded-lg border-2 text-[10px] font-bold flex items-center justify-center px-2 text-center transition ${
                    i === current
                      ? "border-[hsl(var(--brimi-blue))] bg-[hsl(var(--brimi-blue))]/10"
                      : "border-border bg-card hover:border-foreground/30"
                  }`}
                  title={s.title}
                >
                  {i + 1}. {s.type}
                </button>
              ))}
            </div>

            {slide?.notes && (
              <div className="mt-5 p-4 rounded-2xl bg-muted/60 border border-border">
                <div className="text-xs font-black uppercase tracking-widest text-muted-foreground mb-1.5 flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5" /> Speaker notes
                </div>
                <p className="text-sm leading-relaxed">{slide.notes}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="tactile-card p-10 text-center">
            <p className="text-muted-foreground mb-4">No slides yet.</p>
            <button
              onClick={regenerate}
              className="pill-btn bg-[hsl(var(--brimi-blue))] text-white px-5 py-3"
            >
              Generate slides
            </button>
          </div>
        )}

        {/* Action bar */}
        <div className="mt-6 flex flex-wrap gap-3">
          <button
            data-testid="download-pptx-btn"
            onClick={downloadAIPPTX}
            className="pill-btn bg-[hsl(var(--brimi-blue))] hover:bg-[hsl(var(--brimi-blue))]/90 text-white px-5 py-3"
          >
            <Download className="w-4 h-4" /> Download .pptx
          </button>
          <button
            data-testid="regenerate-btn"
            onClick={regenerate}
            disabled={regenerating}
            className="pill-btn bg-foreground/5 hover:bg-foreground/10 px-5 py-3 disabled:opacity-50"
          >
            {regenerating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RotateCcw className="w-4 h-4" />
            )}
            Regenerate with AI
          </button>
          {isOwner && (
            <label
              className={`pill-btn cursor-pointer px-5 py-3 ${
                uploading
                  ? "bg-foreground/10"
                  : "bg-[hsl(var(--brimi-coral))] hover:bg-[hsl(var(--brimi-coral))]/90 text-white"
              }`}
              data-testid="upload-label"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              {uploading ? "Uploading…" : "Upload your own .pptx / .pdf"}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pptx,.pdf"
                className="hidden"
                onChange={onFile}
                disabled={uploading}
                data-testid="upload-input"
              />
            </label>
          )}
        </div>

        {/* Python Quick Reference — only shown for Class XI/XII Python chapters */}
        <div className="mt-8">
          <PythonQuickRef topicId={topicId} accent={accent} />
        </div>
      </main>
    </div>
  );
}

